// /components/VideoEmbed/TikTokEmbed.js
import React, { useEffect, useRef } from 'react';

/**
 * extractTikTokVideoId
 * ----------------------
 * Extracts the TikTok video ID from a URL.
 * For example, given:
 *   https://www.tiktok.com/@infinity__shuffle/video/7174988338087152938?q=w%20step&t=1738480400307
 * It returns: "7174988338087152938"
 */
function extractTikTokVideoId(url) {
  const match = url.match(/\/video\/(\d+)/);
  return match ? match[1] : null;
}

/**
 * TikTokEmbed Component
 * -----------------------
 * Renders a TikTok embed given a video URL.
 * - Dynamically loads TikTok’s embed script if not already present.
 * - Uses a MutationObserver to watch for changes in the embed's DOM and triggers
 *   a window resize event so parent components can adjust their layout.
 */
const TikTokEmbed = ({ url }) => {
  const videoId = extractTikTokVideoId(url);
  const embedRef = useRef(null);

  useEffect(() => {
    // Dynamically load TikTok's embed script if it's not already loaded.
    if (!document.querySelector('script[src="https://www.tiktok.com/embed.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    }
    
    // Setup a MutationObserver on the embed element to detect DOM changes.
    const observer = new MutationObserver((mutationsList) => {
      // When the blockquote's children change (embed content loaded), dispatch a resize event.
      window.dispatchEvent(new Event('resize'));
    });

    if (embedRef.current) {
      observer.observe(embedRef.current, { childList: true, subtree: true });
    }
    
    // Clean up the observer on component unmount.
    return () => {
      observer.disconnect();
    };
  }, [url]);

  if (!videoId) return <p>Invalid TikTok URL</p>;

  return (
    <blockquote
      ref={embedRef}
      className="tiktok-embed"
      cite={url}
      data-video-id={videoId}
      style={{ maxWidth: '605px', minWidth: '325px' }}
    >
      <section> </section>
    </blockquote>
  );
};

export default TikTokEmbed;
